/**
 * シーングラフ（部屋・調査対象のメッシュ）の組み立て。
 *
 * 【なぜ scene.ts から切り出したか】
 * OfficeScene は WebGLRenderer を持つためテストから生成できない。
 * 一方で「レティクルで対象を狙えるか」は 3D の形状だけで決まるので、
 * レンダラに依存しないここを独立させ、テストから同じ形状を組み立てられるようにした。
 * （tests/engine/aim-reachability.test.ts が本番と同じこの関数を使う）
 *
 * 【当たり判定と見た目の分離】
 * Interactable.size は型定義のとおり「当たり判定の大きさ」であり、
 * position はその中心（visual ごとの anchor 規則で床基準・天板基準にもなる）。
 * 見た目のメッシュは飾りが付いて大きさがまちまちになるため、
 * レイキャストは各グループに 1 つだけ入れた不可視の「当たり判定ボックス」だけを見る。
 * こうしないと、飾り（PC の机や、ラックの筐体）が中身の機器の手前に来て
 * 「見えているのに狙えない」状態が起きる。
 */

import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { buildOfficeDetails, addWorkstationDetails, addCable } from './officeDetails';
import { sharedDeskFor, sharedChairOffset } from './furnitureLayout';
import { finishIsOffice } from './isOfficeFinish';
import { buildFittedOfficeDoor, OFFICE_LEAF_HEIGHT, OFFICE_LEAF_WIDTH, OFFICE_LEAF_INSET, OFFICE_HINGE_X, officeDoorFrame } from './officeDoor';
import type { NetworkWorld } from '../types/network';
import type { Interactable } from '../types/scenario';
import { getLinkStatus } from '../sim/topology';
import {
  WALL_HEIGHT,
  WALL_THICKNESS,
  roomLocalToWorld,
  type FloorMaterial,
  type OfficeLayout,
  type WalkRect,
} from './layout';

export const COLORS = {
  carpet: 0x4a5568,
  tile: 0xb9bec7,
  wall: 0xe6e3dc,
  wallTrim: 0xcfcabf,
  ceiling: 0xf4f4f6,
  desk: 0xc08a52,
  pc: 0x2f3640,
  screen: 0x1b2733,
  switchBody: 0x3a4250,
  rack: 0x2b2f38,
  person: 0xd9a066,
  personBody: 0x5a7fa8,
  note: 0xf2ead3,
  cable: 0x2d6a4f,
  printer: 0xdcdcdc,
  microwave: 0x9aa0a6,
  poster: 0xfff8e1,
  door: 0x8d6e50,
  ap: 0xf1f1f1,
  patch: 0x555c66,
};

/**
 * 当たり判定ボックスの最小の一辺。
 * 壁の LAN 端子（0.12m）やデスク下のスイッチ（高さ 0.04m）は
 * 実寸のままだとレティクルに乗らないため、狙える最小限まで太らせる。
 * ラックに並ぶ機器の間隔（0.22m）より小さくして、隣同士が重ならないようにしてある。
 */
export const MIN_PICK_SIZE = 0.16;

/**
 * server-rack の当たり判定は奥行きの後ろ側だけにする。
 * 前面を空けておくことで、ラックに載っている機器（スイッチ等）が
 * 筐体の当たり判定に隠されず、正面から個別に狙える。
 */
export const RACK_PICK_DEPTH_RATIO = 0.55;

/** この高さ以上の server-rack は「機器を載せる筐体」として扱う */
export const RACK_ENCLOSURE_MIN_HEIGHT = 1.0;

/** 机の当たり判定に使う天板の厚み（脚の間の空間は判定に含めない） */
export const DESK_TOP_THICKNESS = 0.18;

export interface InteractableHandle {
  id: string;
  /** 見た目も含むグループ */
  object: THREE.Object3D;
  /** レイキャストの対象（不可視の当たり判定ボックス） */
  pickTarget: THREE.Object3D;
  interactable: Interactable;
}

export interface InteractableBuild {
  root: THREE.Group;
  handles: InteractableHandle[];
  ledsByLink: Map<string, THREE.MeshStandardMaterial[]>;
}

type SurfaceOptions = Omit<THREE.MeshStandardMaterialParameters, 'color'>;

function boxMesh(
  width: number,
  height: number,
  depth: number,
  color: number,
  options: SurfaceOptions = {},
): THREE.Mesh<THREE.BoxGeometry, THREE.MeshStandardMaterial> {
  const geometry = new RoundedBoxGeometry(Math.max(0.001, width), Math.max(0.001, height), Math.max(0.001, depth), 2,
    Math.min(0.012, width / 5, height / 5, depth / 5));
  const material = new THREE.MeshStandardMaterial({
    color,
    roughness: 0.72,
    metalness: 0.04,
    ...options,
  });
  const mesh = new THREE.Mesh(geometry, material);
  if (color === COLORS.desk || color === COLORS.door) material.userData.oak = true;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

// ---------------------------------------------------------------
// 当たり判定の基準
// ---------------------------------------------------------------

/**
 * visual ごとの「position が箱のどこを指すか」。
 *   center  : 箱の中心（既定）
 *   base    : 箱の底（床置き。人）
 *   top     : 箱の上面（天板の高さ。机）
 *   surface : その上に物を置く面（PC が載っている机の天板）
 */
export type Anchor = 'center' | 'base' | 'top' | 'surface';

export function anchorOf(visual: Interactable['visual']): Anchor {
  switch (visual) {
    case 'person':
      return 'base';
    case 'desk':
      return 'top';
    case 'pc':
      return 'surface';
    default:
      return 'center';
  }
}

/** visual ごとの既定サイズ（size 未指定のとき） */
export function defaultSize(visual: Interactable['visual']): [number, number, number] {
  switch (visual) {
    case 'desk':
      return [2.0, 0.75, 1.0];
    case 'pc':
      return [0.5, 0.35, 0.25];
    case 'switch':
      return [1.2, 0.45, 0.6];
    case 'patchpanel':
      return [1.4, 0.4, 0.3];
    case 'cable':
      return [0.7, 0.25, 0.7];
    case 'ap':
      return [0.5, 0.1, 0.5];
    case 'door':
      return [1.0, 2.0, 0.1];
    case 'person':
      return [0.6, 1.7, 0.6];
    case 'note':
      return [0.5, 0.06, 0.35];
    case 'server-rack':
      return [1.2, 2.0, 1.0];
    case 'printer':
      return [1.1, 0.9, 0.8];
    case 'microwave':
      return [0.9, 0.55, 0.7];
    case 'poster':
      return [1.4, 1.0, 0.05];
    default:
      return [0.6, 0.6, 0.6];
  }
}

/** PC のモニタは机の天板からこの高さだけ上に置く */
const PC_MONITOR_LIFT = 0.3;

/**
 * グループ原点（= item.position）から見た当たり判定ボックス。
 * 見た目のメッシュもこの箱に合わせて組み立てる。
 */
export interface PickBox {
  /** 中心（グループ原点からの相対位置） */
  center: [number, number, number];
  /** 各辺の長さ */
  size: [number, number, number];
}

/** 前面が開いた筐体として扱うラックか（中に機器を載せる大きさのもの） */
export function isRackEnclosure(item: Interactable): boolean {
  if (item.visual !== 'server-rack') return false;
  const [, h] = item.size ?? defaultSize(item.visual);
  return h >= RACK_ENCLOSURE_MIN_HEIGHT;
}

/**
 * グループ原点から見た「見た目の箱の中心 y」。
 * anchor 規則を適用したうえで、床に沈まないように持ち上げる。
 */
export function visualCenterY(item: Interactable): number {
  const [, h] = item.size ?? defaultSize(item.visual);
  const anchor = anchorOf(item.visual);
  const cy =
    anchor === 'base' ? h / 2 : anchor === 'top' ? -h / 2 : anchor === 'surface' ? PC_MONITOR_LIFT : 0;
  const bottom = item.position[1] + cy - h / 2;
  return bottom < 0 ? cy - bottom : cy;
}

export function pickBoxOf(item: Interactable): PickBox {
  const [w, h, d] = item.size ?? defaultSize(item.visual);
  let cy = visualCenterY(item);

  // 狙える最小の大きさまで太らせる（見た目は変えない）
  const size: [number, number, number] = [
    Math.max(w, MIN_PICK_SIZE),
    Math.max(h, MIN_PICK_SIZE),
    Math.max(d, MIN_PICK_SIZE),
  ];

  if (item.visual === 'desk') {
    // 机は天板だけを当たり判定にする。
    // 脚の間の空間まで判定に含めると、机の下に置いたスイッチ（int-sw-sales-desk）を
    // 机が丸ごと飲み込んでしまい、そちらが狙えなくなる。
    const slab = Math.min(size[1], DESK_TOP_THICKNESS);
    cy += size[1] / 2 - slab / 2;
    size[1] = slab;
  }

  let cz = 0;
  if (isRackEnclosure(item)) {
    // 前面を空けて、ラックに載る機器を隠さない
    const depth = size[2] * RACK_PICK_DEPTH_RATIO;
    cz = -(size[2] - depth) / 2;
    size[2] = depth;
  }

  // 最小サイズまで太らせた結果、床に沈まないように持ち上げる
  const bottom = item.position[1] + cy - size[1] / 2;
  if (bottom < 0) cy -= bottom;

  return { center: [0, cy, cz], size };
}

// ---------------------------------------------------------------
// 建物
// ---------------------------------------------------------------

export function buildOfficeMeshes(layout: OfficeLayout): THREE.Group {
  const root = new THREE.Group();
  root.add(buildOfficeDetails(layout));

  for (const rect of layout.walkRects) {
    addFloor(root, rect);
    if (rect.kind === 'room' || rect.kind === 'corridor') addCeiling(root, rect);
  }

  for (const wall of layout.walls) {
    const dx = wall.x1 - wall.x0;
    const dz = wall.z1 - wall.z0;
    const length = Math.hypot(dx, dz);
    if (length < 0.01) continue;
    // 高さ 1m 程度の壁は階段の手すり。壁一枚にせず、腰壁と金属レールに分ける。
    if (wall.height <= 1.05) {
      addHandrail(root, wall, length, dx, dz);
      continue;
    }

    const mesh = boxMesh(length, wall.height, WALL_THICKNESS, COLORS.wall, { roughness: 0.96 });
    mesh.position.set((wall.x0 + wall.x1) / 2, wall.y + wall.height / 2, (wall.z0 + wall.z1) / 2);
    mesh.rotation.y = -Math.atan2(dz, dx);
    root.add(mesh);

    // 壁際の幅木。床と壁の境界を作るだけでも奥行きが読み取りやすくなる。
    const trim = boxMesh(length, 0.12, WALL_THICKNESS + 0.025, COLORS.wallTrim, {
      roughness: 0.8,
    });
    trim.position.set((wall.x0 + wall.x1) / 2, wall.y + 0.06, (wall.z0 + wall.z1) / 2);
    trim.rotation.y = mesh.rotation.y;
    root.add(trim);
  }

  // ドア枠（上枠＋縦枠）。通れる場所を現実のスケールで読み取れるようにする。
  for (const room of Object.values(layout.rooms)) {
    const fitting = room.id === 'is-office' ? officeDoorFrame(room) : { z: room.doorPosition[2], depth: WALL_THICKNESS + 0.05 };
    const frame = boxMesh(2.4, 0.25, fitting.depth, COLORS.door);
    frame.name = `door-frame-header:${room.id}`;
    frame.position.set(room.doorPosition[0], room.y + WALL_HEIGHT - 0.4, fitting.z);
    root.add(frame);
    for (const side of [-1, 1]) {
      const post = boxMesh(0.14, WALL_HEIGHT - 0.45, fitting.depth, COLORS.door, {
        roughness: 0.55,
      });
      post.position.set(
        room.doorPosition[0] + side * 1.08,
        room.y + (WALL_HEIGHT - 0.45) / 2,
        fitting.z,
      );
      post.name = `door-frame-post:${room.id}:${side}`;
      root.add(post);
    }
  }

  finishIsOffice(root, layout);
  return root;
}

function addFloor(root: THREE.Group, rect: WalkRect): void {
  const width = rect.x1 - rect.x0;
  const depth = rect.z1 - rect.z0;
  if (width <= 0 || depth <= 0) return;

  const isSlope = rect.yAtX0 !== rect.yAtX1;
  const rise = rect.yAtX1 - rect.yAtX0;
  const length = isSlope ? Math.hypot(width, rise) : width;
  // The albedo texture already contains the floor color; multiplying it by blue made the floor black.
  const color = 0xffffff;
  const mesh = boxMesh(length, 0.2, depth, color, {
    roughness: rect.material === 'carpet' ? 1 : 0.72,
    metalness: 0,
    map: makeFloorTexture(rect.material, width, depth),
  });
  if (rect.material === 'carpet') mesh.material.userData.carpetSize = [width, depth];
  mesh.castShadow = false;

  if (isSlope) {
    mesh.position.set(
      (rect.x0 + rect.x1) / 2,
      (rect.yAtX0 + rect.yAtX1) / 2 - 0.1,
      (rect.z0 + rect.z1) / 2,
    );
    mesh.rotation.z = Math.atan2(rise, width);
  } else {
    mesh.position.set((rect.x0 + rect.x1) / 2, rect.y - 0.1, (rect.z0 + rect.z1) / 2);
  }
  root.add(mesh);
}

function addCeiling(root: THREE.Group, rect: WalkRect): void {
  const width = rect.x1 - rect.x0;
  const depth = rect.z1 - rect.z0;
  if (width <= 0 || depth <= 0) return;
  const mesh = boxMesh(width, 0.12, depth, COLORS.ceiling, { roughness: 0.92, metalness: 0 });
  mesh.name = `ceiling:${rect.id}`;
  mesh.position.set((rect.x0 + rect.x1) / 2, rect.y + WALL_HEIGHT, (rect.z0 + rect.z1) / 2);
  // 天井が太陽光のシャドウマップを完全に遮ると室内が一様に暗くなるため、
  // 影を受けるだけにする。室内の陰影は家具と壁が落とす。
  mesh.castShadow = false;
  root.add(mesh);


  // 蛍光灯風の面発光パネル。実ライトは数を抑え、見た目の灯具は等間隔に並べる。
  const fixtureCount = Math.max(1, Math.ceil(width / 6));
  for (let i = 0; i < fixtureCount; i++) {
    const x = rect.x0 + ((i + 0.5) / fixtureCount) * width;
    const panel = boxMesh(Math.min(1.3, width * 0.35), 0.035, Math.min(0.42, depth * 0.18), 0xfff7df, {
      roughness: 0.35,
      metalness: 0,
      emissive: 0xffe7b0,
      emissiveIntensity: 2.2,
    });
    panel.position.set(x, rect.y + WALL_HEIGHT - 0.085, (rect.z0 + rect.z1) / 2);
    panel.castShadow = false;
    root.add(panel);

    // 長い廊下も暗部が連続しないよう、灯具ごとに弱い局所光を置く。
    const light = new THREE.PointLight(0xf2f5ff, 15, 7, 2);
    light.position.set(x, rect.y + WALL_HEIGHT - 0.3, (rect.z0 + rect.z1) / 2);
    root.add(light);
  }
}

/** 床材の細かな模様。画像アセットなしで、Node 上の形状テストでも生成できる。 */
function makeFloorTexture(material: FloorMaterial, width: number, depth: number): THREE.DataTexture {
  const size = 32;
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const offset = (y * size + x) * 4;
      if (material === 'tile') {
        const grout = x <= 1 || y <= 1;
        const variation = ((x * 17 + y * 29) % 7) - 3;
        const base = grout ? 166 : 198 + variation;
        data[offset] = base;
        data[offset + 1] = base + (grout ? 1 : 2);
        data[offset + 2] = base + (grout ? 3 : 5);
      } else {
        const grain = ((x * 13 + y * 31 + (x ^ y) * 7) % 17) - 8;
        data[offset] = 72 + grain;
        data[offset + 1] = 85 + grain;
        data[offset + 2] = 103 + grain;
      }
      data[offset + 3] = 255;
    }
  }
  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(Math.max(1, width / (material === 'tile' ? 1.2 : 2.2)), Math.max(1, depth / (material === 'tile' ? 1.2 : 2.2)));
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  texture.needsUpdate = true;
  return texture;
}

function addHandrail(
  root: THREE.Group,
  wall: { x0: number; z0: number; x1: number; z1: number; y: number; height: number },
  length: number,
  dx: number,
  dz: number,
): void {
  const angle = -Math.atan2(dz, dx);
  const centerX = (wall.x0 + wall.x1) / 2;
  const centerZ = (wall.z0 + wall.z1) / 2;
  const metal = { roughness: 0.3, metalness: 0.75 } satisfies SurfaceOptions;
  const rail = boxMesh(length, 0.07, 0.07, 0x747d86, metal);
  rail.position.set(centerX, wall.y + wall.height, centerZ);
  rail.rotation.y = angle;
  root.add(rail);
  const posts = Math.max(2, Math.ceil(length / 1.4) + 1);
  for (let i = 0; i < posts; i++) {
    const t = posts === 1 ? 0 : i / (posts - 1);
    const post = boxMesh(0.055, wall.height, 0.055, 0x747d86, metal);
    post.position.set(
      THREE.MathUtils.lerp(wall.x0, wall.x1, t),
      wall.y + wall.height / 2,
      THREE.MathUtils.lerp(wall.z0, wall.z1, t),
    );
    root.add(post);
  }
}

// ---------------------------------------------------------------
// 調査対象
// ---------------------------------------------------------------

export function buildInteractableMeshes(
  layout: OfficeLayout,
  interactables: Interactable[],
  world: NetworkWorld,
): InteractableBuild {
  const root = new THREE.Group();
  const handles: InteractableHandle[] = [];
  const ledsByLink = new Map<string, THREE.MeshStandardMaterial[]>();

  for (const item of interactables) {
    const room = layout.rooms[item.roomId];
    if (!room) {
      console.warn(`[scene] interactable ${item.id} の部屋 ${item.roomId} がありません`);
      continue;
    }
    const group = new THREE.Group();
    const [x, y, z] = roomLocalToWorld(room, item.position);
    group.position.set(x, y, z);
    // The room-specific finish mounts the notice on its board and places the
    // open door at the actual doorway. Transform the pick proxy together with the visual.
    if (room.id === 'is-office' && item.id === 'int-poster-rules') {
      group.position.x = room.rect.x0 + 0.225;
      group.rotation.y = Math.PI / 2;
    }
    if (room.id === 'is-office' && item.id === 'int-door-is') {
      group.position.x = room.doorPosition[0] + OFFICE_HINGE_X;
      group.position.z = room.rect.z1 - OFFICE_LEAF_INSET;
    }
    buildVisual(group, item, world, ledsByLink, interactables);

    // レイキャスト用の不可視ボックス（見た目より少しだけ大きく、必ず 1 つ）
    const box = pickBoxOf(item);
    if (room.id === 'is-office' && item.id === 'int-door-is') {
      box.size[1] = OFFICE_LEAF_HEIGHT;
      box.size[2] = OFFICE_LEAF_WIDTH;
      box.center[1] = 0.01 + OFFICE_LEAF_HEIGHT / 2 - item.position[1];
    }
    const pickTarget = new THREE.Mesh(
      new THREE.BoxGeometry(box.size[0], box.size[1], box.size[2]),
      new THREE.MeshBasicMaterial({ visible: false }),
    );
    pickTarget.position.set(box.center[0], box.center[1], box.center[2]);
    pickTarget.name = `pick:${item.id}`;
    pickTarget.userData.pickBox = true;
    group.add(pickTarget);

    group.userData.interactableId = item.id;
    root.add(group);
    handles.push({ id: item.id, object: group, pickTarget, interactable: item });
  }

  return { root, handles, ledsByLink };
}

/**
 * 見た目のメッシュ。すべて「当たり判定ボックスの中に収まる」ように置く。
 * 以前は visual ごとに独自の y オフセットを足していたため、
 * データの position と実際の見た目が食い違い（天井の AP が y=4.8m に飛ぶ、
 * ラックが床から 0.9m 浮くなど）、狙えない対象が生まれていた。
 */
function buildVisual(
  group: THREE.Group,
  item: Interactable,
  world: NetworkWorld,
  ledsByLink: Map<string, THREE.MeshStandardMaterial[]>,
  interactables: Interactable[],
): void {
  const [w, h, d] = item.size ?? defaultSize(item.visual);
  // 見た目の箱の中心（当たり判定ボックスと同じ規則。最小サイズの膨らましは見た目には効かせない）
  const y0 = visualCenterY(item);

  switch (item.visual) {
    case 'desk': {
      const furniture = new THREE.Group();
      furniture.name = 'asset-slot:desk';
      furniture.userData.assetSize = [w, h, d];
      furniture.userData.assetOffset = [0, y0 - h / 2, 0];
      group.add(furniture);
      const top = boxMesh(w, 0.08, d, COLORS.desk, { roughness: 0.58 });
      top.position.y = y0 + h / 2 - 0.04;
      furniture.add(top);
      for (const sx of [-1, 1]) {
        for (const sz of [-1, 1]) {
          const leg = boxMesh(0.065, h - 0.08, 0.065, 0x5c6268, {
            roughness: 0.32,
            metalness: 0.68,
          });
          leg.position.set(sx * (w / 2 - 0.1), y0 - 0.04, sz * (d / 2 - 0.1));
          furniture.add(leg);
        }
      }
      const modesty = boxMesh(w * 0.82, h * 0.38, 0.035, 0xa87443, { roughness: 0.68 });
      modesty.position.set(0, y0 + h * 0.08, -d / 2 + 0.055);
      furniture.add(modesty);
      break;
    }
    case 'pc': {
      // position は「PC が載っている天板の高さ」。机は下、モニタは上に置く
      const deskW = Math.max(1.0, w + 0.7);
      const deskD = Math.max(0.6, d + 0.4);
      const sharedDesk = sharedDeskFor(item, interactables);
      const furniture = new THREE.Group();
      furniture.name = sharedDesk ? 'shared-desk-placeholder' : 'asset-slot:desk';
      furniture.visible = !sharedDesk;
      furniture.userData.assetSize = [deskW, 0.75, deskD];
      furniture.userData.assetOffset = [0, -0.75, 0];
      group.add(furniture);
      const top = boxMesh(deskW, 0.06, deskD, COLORS.desk, { roughness: 0.58 });
      top.position.y = -0.03;
      furniture.add(top);
      for (const sx of [-1, 1]) {
        for (const sz of [-1, 1]) {
          const leg = boxMesh(0.055, 0.7, 0.055, 0x596168, {
            roughness: 0.3,
            metalness: 0.72,
          });
          leg.position.set(sx * (deskW / 2 - 0.08), -0.41, sz * (deskD / 2 - 0.08));
          furniture.add(leg);
        }
      }
      const body = boxMesh(w, h, Math.min(0.06, d), 0x20252c, {
        roughness: 0.38,
        metalness: 0.2,
      });
      body.position.set(0, y0, -0.04);
      group.add(body);
      const screen = boxMesh(w * 0.88, h * 0.8, 0.012, 0x16334a, {
        roughness: 0.18,
        metalness: 0.05,
        emissive: 0x0b426b,
        emissiveIntensity: 0.65,
      });
      screen.position.set(0, y0, -0.005);
      group.add(screen);
      const stand = boxMesh(0.1, y0 - h / 2, 0.1, COLORS.pc);
      stand.position.set(0, (y0 - h / 2) / 2, -0.04);
      group.add(stand);
      const keyboard = boxMesh(w * 0.9, 0.02, 0.16, 0x3d4450);
      keyboard.position.set(0, 0.02, 0.22);
      group.add(keyboard);
      const mouse = boxMesh(0.055, 0.025, 0.08, 0x252a30, { roughness: 0.42 });
      mouse.position.set(w * 0.62, 0.025, 0.22);
      group.add(mouse);
      const tower = boxMesh(0.18, 0.5, 0.42, 0x242a31, {
        roughness: 0.52,
        metalness: 0.22,
      });
      tower.position.set(-deskW / 2 + 0.15, -0.29, -0.04);
      group.add(tower);
      addWorkstationDetails(group, deskW, deskD, item.id === 'int-pc-player');
      if (sharedDesk) {
        const chair = group.getObjectByName('asset-slot:chair');
        if (chair) chair.position.z = sharedChairOffset(item, sharedDesk);
      }
      // Small screen lines and keyboard keycaps give recognizable working scale.
      for (let row = 0; row < 4; row++) {
        const line = boxMesh(w * (0.62 - row * 0.08), 0.009, 0.005, 0x8bb3c0,
          { emissive: 0x547c94, emissiveIntensity: 0.35 });
        line.position.set(-w * 0.07, y0 + h * (0.23 - row * 0.13), 0.005);
        group.add(line);
        for (let col = 0; col < 10; col++) {
          const key = boxMesh(w * 0.071, 0.006, 0.024, 0x657077);
          key.position.set((col - 4.5) * w * 0.081, 0.032, 0.169 + row * 0.031);
          group.add(key);
        }
      }
      break;
    }
    case 'switch': {
      const chassis = assetSlot(group, 'switch', [w, h, d], [0, y0, 0]);
      const body = boxMesh(w, h, d, COLORS.switchBody, { roughness: 0.4, metalness: 0.52 });
      body.position.y = y0;
      chassis.add(body);
      addPortLeds(group, item, world, ledsByLink, w, y0, h, d);
      break;
    }
    case 'patchpanel': {
      const body = boxMesh(w, h, d, COLORS.patch, { roughness: 0.4, metalness: 0.48 });
      body.position.y = y0;
      group.add(body);
      const face = boxMesh(w * 0.8, h * 0.6, 0.01, 0x9aa4b2);
      face.position.set(0, y0, d / 2 + 0.006);
      group.add(face);
      break;
    }
    case 'cable': {
      const coil = new THREE.Mesh(
        new THREE.TorusGeometry(Math.max(0.08, Math.min(w, d) / 2.4), Math.min(0.05, h / 2), 6, 14),
        new THREE.MeshStandardMaterial({ color: COLORS.cable, roughness: 0.78 }),
      );
      coil.castShadow = true;
      coil.receiveShadow = true;
      coil.rotation.x = Math.PI / 2;
      coil.position.y = y0;
      group.add(coil);
      const plug = boxMesh(0.1, 0.05, 0.16, 0x9fd6b4);
      plug.position.set(w / 2, y0, 0);
      group.add(plug);
      break;
    }
    case 'ap': {
      const chassis = assetSlot(group, 'ap', [w, h, d], [0, y0, 0]);
      const body = boxMesh(w, h, d, COLORS.ap, { roughness: 0.42 });
      body.position.y = y0;
      chassis.add(body);
      // 天井から吊っている見た目（AP が宙に浮いて見えないように）
      const ceilingGap = WALL_HEIGHT - (item.position[1] + y0 + h / 2);
      if (ceilingGap > 0.05) {
        const pole = boxMesh(0.05, ceilingGap, 0.05, 0xb8bcc4);
        pole.position.y = y0 + h / 2 + ceilingGap / 2;
        group.add(pole);
      }
      const led = boxMesh(0.05, 0.02, 0.05, 0x2ecc71);
      const ledMat = led.material;
      ledMat.userData.statusLed = true;
      ledMat.emissive = new THREE.Color(0x2ecc71);
      ledMat.emissiveIntensity = 0.9;
      led.position.set(0, y0 - h / 2 - 0.01, d / 2 - 0.08);
      group.add(led);
      break;
    }
    case 'door': {
      if (item.id === 'int-door-is') {
        group.add(buildFittedOfficeDoor(item.position[1]));
        break;
      }
      const body = boxMesh(w, h, d, COLORS.door, { roughness: 0.55 });
      body.position.y = y0;
      group.add(body);
      // 面材に浅いパネルを重ね、単なる板ではなく室内扉に見せる。
      const panelDepth = Math.min(w, d) + 0.012;
      for (const offset of [-0.27, 0.3]) {
        const inset = boxMesh(
          w <= d ? w + 0.012 : w * 0.68,
          h * 0.3,
          w <= d ? d * 0.68 : panelDepth,
          0x795a42,
          { roughness: 0.6 },
        );
        inset.position.set(0, y0 + offset * h, w <= d ? 0 : d / 2 + 0.008);
        group.add(inset);
      }
      // 取っ手（薄いほうの面に付ける）
      const knob = boxMesh(0.06, 0.06, 0.06, 0xe0c268);
      if (w <= d) knob.position.set(w / 2 + 0.03, y0, d / 2 - 0.18);
      else knob.position.set(w / 2 - 0.18, y0, d / 2 + 0.03);
      group.add(knob);
      break;
    }
    case 'person': {
      buildPerson(group, w, h, item.id);
      break;
    }
    case 'note': {
      const body = boxMesh(w, h, d, COLORS.note);
      body.position.y = y0;
      body.rotation.y = 0.3;
      group.add(body);
      break;
    }
    case 'server-rack': {
      const large = h >= RACK_ENCLOSURE_MIN_HEIGHT;
      const chassis = assetSlot(group, large ? 'rack' : 'server', [w, h, d], [0, large ? y0 - h / 2 : y0, 0]);
      buildRack(chassis, w, h, d, y0);
      // Status indicators belong to the game, not to a replaceable GLB.
      for (const child of [...chassis.children]) {
        if (child instanceof THREE.Mesh && !Array.isArray(child.material) && child.material.userData.statusLed) group.add(child);
      }
      break;
    }
    case 'printer': {
      const body = boxMesh(w, h * 0.7, d, COLORS.printer, { roughness: 0.36 });
      body.position.y = y0 - h * 0.15;
      group.add(body);
      const tray = boxMesh(w * 0.9, h * 0.1, d * 0.5, 0xa8a8a8);
      tray.position.set(0, y0 + h * 0.3, d * 0.1);
      group.add(tray);
      const paper = boxMesh(w * 0.72, 0.012, d * 0.42, 0xf7f4ea, { roughness: 1 });
      paper.position.set(0, y0 + h * 0.37, d * 0.06);
      paper.rotation.x = -0.08;
      group.add(paper);
      const display = boxMesh(w * 0.22, h * 0.09, 0.015, 0x183f54, {
        emissive: 0x0c4968,
        emissiveIntensity: 0.7,
        roughness: 0.2,
      });
      display.position.set(w * 0.25, y0 + h * 0.2, d / 2 + 0.012);
      group.add(display);
      break;
    }
    case 'microwave': {
      const body = boxMesh(w, h, d, COLORS.microwave, { roughness: 0.34, metalness: 0.58 });
      body.position.y = y0;
      group.add(body);
      const win = boxMesh(w * 0.55, h * 0.55, 0.03, 0x30363d);
      win.position.set(-w * 0.12, y0, d / 2 + 0.01);
      group.add(win);
      const display = boxMesh(w * 0.16, h * 0.12, 0.02, 0x12384a, {
        emissive: 0x1ba0c4,
        emissiveIntensity: 0.65,
        roughness: 0.2,
      });
      display.position.set(w * 0.35, y0 + h * 0.15, d / 2 + 0.018);
      group.add(display);
      break;
    }
    case 'poster': {
      const frame = boxMesh(w + 0.055, h + 0.055, Math.max(0.025, d), 0x52575d, {
        roughness: 0.45,
        metalness: 0.35,
      });
      frame.position.y = y0;
      group.add(frame);
      const body = boxMesh(w, h, Math.max(0.02, d) + 0.008, COLORS.poster, { roughness: 0.92 });
      body.position.y = y0;
      group.add(body);
      for (let i = 0; i < 4; i++) {
        const line = boxMesh(w * (0.68 - i * 0.07), Math.max(0.012, h * 0.025), 0.008, 0x7c858b, {
          roughness: 0.8,
        });
        line.position.set(-w * 0.06, y0 + h * (0.22 - i * 0.14), d / 2 + 0.018);
        group.add(line);
      }
      break;
    }
    default: {
      const body = boxMesh(w, h, d, 0x888888);
      body.position.y = y0;
      group.add(body);
    }
  }
}

function assetSlot(parent: THREE.Group, key: string, size: number[], offset: number[]): THREE.Group {
  const slot = new THREE.Group();
  slot.name = `asset-slot:${key}`;
  slot.userData.assetSize = size;
  slot.userData.assetOffset = offset;
  parent.add(slot);
  return slot;
}

/**
 * サーバーラック。
 *
 * 高さ 1m 以上のものは「前面が開いた筐体」として組む。
 * 中に置かれた機器（スイッチ等）が枠に隠れず、正面から 1 台ずつ狙えるようにするため。
 * 小さいものはラックマウント機器そのものなので、ただの箱にする。
 */
function buildRack(group: THREE.Group, w: number, h: number, d: number, y0: number): void {
  if (h < RACK_ENCLOSURE_MIN_HEIGHT) {
    const body = boxMesh(w, h, d, COLORS.rack, { roughness: 0.34, metalness: 0.58 });
    body.position.y = y0;
    group.add(body);
    const face = boxMesh(w * 0.85, h * 0.5, 0.02, 0x4b515c);
    face.position.set(0, y0, d / 2 + 0.011);
    group.add(face);
    const led = boxMesh(0.04, 0.04, 0.02, 0x2ecc71);
    const ledMat = led.material;
    ledMat.userData.statusLed = true;
    ledMat.emissive = new THREE.Color(0x2ecc71);
    ledMat.emissiveIntensity = 0.9;
    led.position.set(w * 0.36, y0, d / 2 + 0.02);
    group.add(led);
    return;
  }

  const t = 0.06; // 板の厚み
  const rackFinish = { roughness: 0.32, metalness: 0.62 } satisfies SurfaceOptions;
  const back = boxMesh(w, h, t, COLORS.rack, rackFinish);
  back.position.set(0, y0, -d / 2 + t / 2);
  group.add(back);
  for (const sx of [-1, 1]) {
    const side = boxMesh(t, h, d, COLORS.rack, rackFinish);
    side.position.set(sx * (w / 2 - t / 2), y0, 0);
    group.add(side);
  }
  const top = boxMesh(w, t, d, COLORS.rack, rackFinish);
  top.position.set(0, y0 + h / 2 - t / 2, 0);
  group.add(top);
  const bottom = boxMesh(w, t, d, COLORS.rack, rackFinish);
  bottom.position.set(0, y0 - h / 2 + t / 2, 0);
  group.add(bottom);

  // ラックの棚板。中の機器を隠さず、奥行きの手掛かりだけを足す。
  for (let i = 1; i <= 4; i++) {
    const shelf = boxMesh(w - t * 2, 0.025, d * 0.78, 0x424851, rackFinish);
    shelf.position.set(0, y0 - h / 2 + (i / 5) * h, -d * 0.08);
    group.add(shelf);
  }
  for (let i = 0; i < 9; i++) {
    const x = (i - 4) * w * 0.07;
    // Patch leads run down the rack edge, leaving device faces visible for inspection.
    addCable(group, [[x, y0 + h * 0.32, d * 0.37], [x, y0 + h * 0.22, d * 0.49],
      [-w * 0.37, y0 + h * 0.14, d * 0.45], [-w * 0.37, y0 - h * 0.25 + i * 0.013, d * 0.42]],
      i % 2 ? 0x376f93 : 0x448477);
  }
}

/** 簡素な人型でも、頭・胴・手足の分離と陰影で距離や向きが読み取りやすくなる。 */
function buildPerson(group: THREE.Group, width: number, height: number, id: string): void {
  if (id === 'int-takase') {
    const slot = new THREE.Group();
    slot.name = 'character-slot:takase';
    group.add(slot);
    group = slot;
  }
  const skin = new THREE.MeshStandardMaterial({ color: COLORS.person, roughness: 0.82 });
  const colors: Record<string, number> = {
    'int-takase': 0x243449, 'int-murofushi': 0x343c49, 'int-omori': 0xb9aa94,
    'int-saeki': 0x45484b, 'int-hayama': 0x777a7c, 'int-rina': 0x30435c,
  };
  const cloth = new THREE.MeshStandardMaterial({ color: colors[id] ?? COLORS.personBody, roughness: 0.88 });
  const dark = new THREE.MeshStandardMaterial({ color: 0x27313c, roughness: 0.78 });
  const headRadius = Math.min(width * 0.24, height * 0.088);
  const legHeight = height * 0.36;
  const torsoHeight = height * 0.42;

  for (const side of [-1, 1]) {
    const leg = new THREE.Mesh(
      new THREE.CapsuleGeometry(width * 0.105, Math.max(0.12, legHeight - width * 0.21), 4, 8),
      dark,
    );
    leg.position.set(side * width * 0.13, legHeight / 2, 0);
    leg.castShadow = true;
    leg.receiveShadow = true;
    group.add(leg);

    const arm = new THREE.Mesh(
      new THREE.CapsuleGeometry(width * 0.075, torsoHeight * 0.72, 4, 8),
      cloth,
    );
    arm.position.set(side * width * 0.39, legHeight + torsoHeight * 0.54, 0);
    arm.rotation.z = side * 0.06;
    arm.castShadow = true;
    arm.receiveShadow = true;
    group.add(arm);
  }

  const torso = new THREE.Mesh(
    new THREE.CapsuleGeometry(width * 0.29, Math.max(0.15, torsoHeight - width * 0.42), 5, 12),
    cloth,
  );
  torso.position.y = legHeight + torsoHeight / 2;
  torso.castShadow = true;
  torso.receiveShadow = true;
  group.add(torso);

  const head = new THREE.Mesh(new THREE.SphereGeometry(headRadius, 20, 14), skin);
  head.scale.set(0.88, 1.2, 0.94);
  head.position.y = height - headRadius;
  head.castShadow = true;
  head.receiveShadow = true;
  group.add(head);

  const hair = new THREE.Mesh(
    new THREE.SphereGeometry(headRadius * 1.015, 20, 10, 0, Math.PI * 2, 0, Math.PI * 0.52),
    dark,
  );
  hair.position.copy(head.position);
  hair.scale.copy(head.scale);
  hair.castShadow = true;
  group.add(hair);

  const nose = boxMesh(headRadius * 0.18, headRadius * 0.22, headRadius * 0.2, COLORS.person, {
    roughness: 0.82,
  });
  nose.position.set(0, head.position.y - headRadius * 0.03, headRadius * 0.96);
  group.add(nose);
  for (const side of [-1, 1]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(headRadius * 0.061, 12, 8), dark);
    eye.position.set(side * headRadius * 0.34, head.position.y + headRadius * 0.12, headRadius * 0.86);
    group.add(eye);
    const hand = new THREE.Mesh(new THREE.SphereGeometry(width * 0.07, 14, 10), skin);
    hand.scale.y = 1.45;
    hand.position.set(side * width * 0.4, legHeight + 0.05, 0);
    group.add(hand);
    const shoe = boxMesh(width * 0.2, 0.08, width * 0.43, 0x242628);
    shoe.position.set(side * width * 0.13, 0.05, 0.04);
    group.add(shoe);
  }
  const shirt = boxMesh(width * 0.19, torsoHeight * 0.68, 0.015, 0xcbd7db);
  shirt.position.set(0, legHeight + torsoHeight * 0.54, width * 0.287);
  group.add(shirt);
  const badge = boxMesh(0.055, 0.075, 0.012, 0xe4e3d9);
  badge.position.set(width * 0.18, legHeight + torsoHeight * 0.45, width * 0.29);
  group.add(badge);
}

/** スイッチのポート LED。リンク状態に応じて色を変える */
function addPortLeds(
  group: THREE.Group,
  item: Interactable,
  world: NetworkWorld,
  ledsByLink: Map<string, THREE.MeshStandardMaterial[]>,
  width: number,
  centerY: number,
  height: number,
  depth: number,
): void {
  if (!item.deviceId) return;
  const device = world.devices[item.deviceId];
  if (!device) return;

  const ports = [...device.ports].sort((a, b) => a.index - b.index);
  const spacing = Math.min(0.09, (width - 0.08) / Math.max(1, ports.length));
  const startX = -((ports.length - 1) * spacing) / 2;
  const size = Math.min(0.05, height * 0.45);

  ports.forEach((port, i) => {
    const opening = boxMesh(Math.min(size * 0.85, spacing * 0.78), size * 0.58, 0.003, 0x10191d);
    opening.position.set(startX + i * spacing, centerY - size * 0.05, depth / 2 + 0.003);
    opening.userData.portIndex = port.index;
    opening.name = `port:${item.deviceId}:${port.index}`;
    group.add(opening);
    const led = boxMesh(Math.min(0.009, size * 0.22), Math.min(0.007, size * 0.18), 0.006, 0x333333);
    led.material.userData.statusLed = true;
    led.position.set(startX + i * spacing, centerY + size * 0.4, depth / 2 + 0.007);
    group.add(led);
    if (port.linkId) {
      const list = ledsByLink.get(port.linkId) ?? [];
      list.push(led.material);
      ledsByLink.set(port.linkId, list);
    }
  });
}

/** リンク状態の変化を LED の色に反映する */
export function refreshLinkLeds(
  ledsByLink: Map<string, THREE.MeshStandardMaterial[]>,
  world: NetworkWorld,
): void {
  for (const [linkId, materials] of ledsByLink) {
    const status = getLinkStatus(world, linkId);
    const color = status.up ? (status.speedMbps >= 1000 ? 0x2ecc71 : 0xf39c12) : 0x2b2f36;
    for (const material of materials) {
      material.color.setHex(color);
      material.emissive.setHex(status.up ? color : 0x000000);
      material.emissiveIntensity = status.up ? 0.85 : 0;
    }
  }
}

/** グループ配下のジオメトリ／マテリアルを破棄して空にする */
export function disposeGroup(group: THREE.Group): void {
  const disposables: THREE.Object3D[] = [];
  group.traverse((child) => disposables.push(child));
  for (const child of disposables) {
    const mesh = child as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    const material = mesh.material;
    const disposeMaterial = (entry: THREE.Material): void => {
      const textured = entry as THREE.Material & { map?: THREE.Texture | null };
      textured.map?.dispose();
      entry.dispose();
    };
    if (Array.isArray(material)) material.forEach(disposeMaterial);
    else if (material) disposeMaterial(material as THREE.Material);
  }
  group.clear();
}
